/**
 * Client-side mirror of `src/types/brandAuthorization.ts`.
 */
export type BrandAuthorizationLevel =
  | 'authorized_dealer'
  | 'reseller'
  | 'special_order'
  | 'former_dealer'
  | 'unauthorized';

export type BrandVerificationStatus = 'unverified' | 'pending' | 'verified' | 'rejected' | 'expired';
export type BrandVerificationMethod =
  | 'self_declared'
  | 'dealer_letter'
  | 'admin_assisted'
  | 'promaster_api'
  | 'brand_directory'
  | 'dealer_directory_stale'
  | 'aputure_locator'
  | 'canon_wtb'
  | 'fujifilm_locator'
  | 'peak_design_locally'
  | 'gopro_locally'
  | 'sony_pricespider'
  | 'hasselblad_partners'
  | 'nisi_locator'
  | 'panasonic_authorized_list'
  | 'rokinon_storeify'
  | 'smallrig_resellers'
  | 'tokina_shopcircle'
  | 'thinktank_shopcircle'
  | 'tiffen_shopcircle'
  | 'tamron_dealers'
  | 'benro_storemapper'
  | 'godox_storemapper'
  | 'promaster_storerocket'
  | 'pelican_locator'
  | 'phottix_storerocket'
  | 'smallhd_cs'
  | 'polaroid_experience_stores'
  | 'leica_locator'
  | 'lomography_stores'
  | 'manfrotto_locator'
  | 'lowepro_locator'
  | 'zeiss_locator'
  | 'laowa_locator'
  | 'superior_seamless_locator';

/** Latest check of the store against the brand's published dealer list */
export interface DealerDirectoryMatch {
  confidence: 'exact' | 'strong' | 'weak' | 'none';
  dealerName: string | null;
  city: string | null;
  state: string | null;
  dealerType: string | null;
  dealerTypes?: string[];
  listDate: string | null;
  sourceUrl: string | null;
  checkedAt?: string | null;
}

export interface BrandAuthorizationProof {
  fileName: string;
  storagePath: string;
  contentType?: string | null;
  uploadedAt?: string | null;
  uploadedBy?: string;
  uploadedByRole?: 'store' | 'admin';
  url?: string;
}

export interface BrandAuthorization {
  id: string;
  organizationId: string;
  brandId: string;
  brandName: string;
  level: BrandAuthorizationLevel;
  channels: { online: boolean; inStore: boolean };
  dealerAccountNumber: string | null;
  /** Reseller only: the distributor or buying group the store buys through */
  distributorName?: string | null;
  /** Reseller only: the store's account number with that distributor */
  distributorAccountNumber?: string | null;
  notes: string | null;
  /** A link the store sent as proof (its locator listing, a dealer list, a portal page) */
  proofLink?: string | null;
  /** The store's note to the Motzu reviewer about the proof it sent */
  proofNote?: string | null;
  verification: {
    status: BrandVerificationStatus;
    method: BrandVerificationMethod;
    submittedAt?: string | null;
    verifiedAt?: string | null;
    verifiedBy?: string | null;
    expiresAt?: string | null;
    reviewNotes?: string | null;
    sourceEmail?: string | null;
    staleListMatch?: {
      confidence: string;
      dealerName?: string | null;
      city?: string | null;
      state?: string | null;
      listAge?: number;
    } | null;
  };
  proof: BrandAuthorizationProof | null;
  directoryMatch?: DealerDirectoryMatch | null;
  /** Admin responses only: tiers the brand gives the store on top of `level`; ended ones are filtered out, not-yet-started ones kept */
  designations?: BrandAuthorizationDesignation[];
  /** Server-computed: verified past expiry reads as expired */
  effectiveStatus?: BrandVerificationStatus;
  /** Server-computed: how Motzu can confirm this brand, from the locator and published-list registries */
  verificationPath?: BrandVerificationPath | null;
  /** Server-computed: the one status the store sees, from this record's own state */
  verificationTag?: BrandAuthorizationTag | null;
}

/** Mirrors `src/types/brandDesignation.ts`. */
export type BrandDesignationTier =
  | 'premier'
  | 'category_specialist'
  | 'cinema_pro'
  | 'education_reseller'
  | 'online_authorized'
  | 'rental_partner'
  | 'solutions_partner'
  | 'brand_owned_store'
  | 'military'
  | 'service_center';

export type BrandProductCategory = 'imaging' | 'cinema' | 'home_av' | 'enterprise_drone' | 'office' | 'sport_optics' | 'scanners';

export type BrandDesignationSource = 'directory' | 'locator' | 'proof' | 'admin' | 'self_declared';

export interface BrandAuthorizationDesignation {
  tier: BrandDesignationTier;
  brandTierCode: string | null;
  categoryScope: BrandProductCategory[];
  source: BrandDesignationSource;
  sourceUrl: string | null;
  listDate: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  recordedAt?: string | null;
  recordedBy?: string | null;
  note: string | null;
}

export const DESIGNATION_TIERS: Array<{ value: BrandDesignationTier; label: string; description: string }> = [
  { value: 'premier', label: 'Premier', description: 'Top status above authorized, usually volume or stocking based' },
  { value: 'category_specialist', label: 'Category specialist', description: 'Authorized for a product line or category' },
  { value: 'cinema_pro', label: 'Cinema / pro video', description: 'Pro video and cinema reseller' },
  { value: 'education_reseller', label: 'Education reseller', description: 'May apply the brand’s education pricing' },
  { value: 'online_authorized', label: 'Online authorized', description: 'Authorized for online sale specifically' },
  { value: 'rental_partner', label: 'Rental partner', description: 'Authorized rental house' },
  { value: 'solutions_partner', label: 'Solutions partner', description: 'Integrator or partner network' },
  { value: 'brand_owned_store', label: 'Brand-owned store', description: 'Retail operated by the manufacturer' },
  { value: 'military', label: 'Military', description: 'Sells to military exchanges' },
  { value: 'service_center', label: 'Service center', description: 'Authorized repair' },
];

export const PRODUCT_CATEGORIES: Array<{ value: BrandProductCategory; label: string }> = [
  { value: 'imaging', label: 'Imaging' },
  { value: 'cinema', label: 'Cinema' },
  { value: 'home_av', label: 'Home AV' },
  { value: 'enterprise_drone', label: 'Enterprise drones' },
  { value: 'office', label: 'Office' },
  { value: 'sport_optics', label: 'Sport optics' },
  { value: 'scanners', label: 'Scanners' },
];

export const DESIGNATION_SOURCE_LABELS: Record<BrandDesignationSource, string> = {
  directory: 'Brand dealer list',
  locator: 'Brand store locator',
  proof: 'Proof on file',
  admin: 'Recorded by Motzu',
  self_declared: 'Store’s own claim',
};

/** Read off a list or locator and rewritten by the next check; an admin cannot edit them. */
export const isMachineDesignation = (d: Pick<BrandAuthorizationDesignation, 'source'>): boolean =>
  d.source === 'directory' || d.source === 'locator';

export const designationTierLabel = (tier: string): string => DESIGNATION_TIERS.find(t => t.value === tier)?.label || tier;

/** "Premier · NPD · Imaging" for a chip. */
export const designationLabel = (d: BrandAuthorizationDesignation): string => {
  const scope = d.categoryScope.map(c => PRODUCT_CATEGORIES.find(p => p.value === c)?.label || c).join(', ');
  return [designationTierLabel(d.tier), d.brandTierCode, scope].filter(Boolean).join(' · ');
};

/** "Brand dealer list, dated 2026-10-13 · until Dec 31, 2026" for a chip tooltip. */
export const designationDetail = (d: BrandAuthorizationDesignation): string => {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' });
  return [
    `${DESIGNATION_SOURCE_LABELS[d.source] || d.source}${d.listDate ? `, dated ${d.listDate}` : ''}`,
    d.effectiveFrom ? `from ${fmt(d.effectiveFrom)}` : '',
    d.effectiveTo ? `until ${fmt(d.effectiveTo)}` : '',
    d.note || '',
  ]
    .filter(Boolean)
    .join(' · ');
};

/** How Motzu can confirm a brand for a store: a live locator check, a
 * published dealer list, or proof the store sends (`letter_only` is the
 * stored id). Mirrors `src/services/dealerLocator/registry.ts`
 * (`verificationPathForBrand`). */
export type BrandVerificationPath = 'locator' | 'dealer_list' | 'letter_only';

/** Mirrors `BrandAuthorizationTag` in `src/types/brandAuthorization.ts`. */
export type BrandAuthorizationTagKind =
  | 'verified'
  | 'list_match'
  | 'proof_under_review'
  | 'expired'
  | 'rejected'
  | 'needs_proof'
  | 'not_required';

export interface BrandAuthorizationTag {
  kind: BrandAuthorizationTagKind;
  listBrandName?: string;
  match?: {
    dealerName: string | null;
    city: string | null;
    state: string | null;
    reason: 'stale_list' | 'partial_name' | 'awaiting_confirmation';
  };
}

type Tone = 'positive' | 'caution' | 'problem' | 'neutral' | 'info';

/** Tone and copy key per tag; copy lives under `store.brandAuthorization.tag` / `.tagHelp`. */
export const VERIFICATION_TAG_META: Record<Exclude<BrandAuthorizationTagKind, 'not_required'>, { tone: Tone; localeKey: string }> = {
  verified: { tone: 'positive', localeKey: 'verified' },
  list_match: { tone: 'info', localeKey: 'listMatch' },
  proof_under_review: { tone: 'info', localeKey: 'proofUnderReview' },
  expired: { tone: 'caution', localeKey: 'expired' },
  rejected: { tone: 'problem', localeKey: 'rejected' },
  needs_proof: { tone: 'caution', localeKey: 'needsProof' },
};

/**
 * The record's tag. The API always sends it; this fallback only covers a
 * response from an older server and maps the bare status the same way.
 */
export const verificationTagOf = (record: BrandAuthorization): BrandAuthorizationTag => {
  if (record.verificationTag) return record.verificationTag;
  const status = verificationStatusOf(record);
  if (status === 'verified') return { kind: 'verified' };
  if (record.level === 'unauthorized' || record.level === 'former_dealer') return { kind: 'not_required' };
  if (status === 'pending') return { kind: 'proof_under_review' };
  if (status === 'rejected' || status === 'expired') return { kind: status };
  return { kind: 'needs_proof' };
};

/** Levels whose claim Motzu verifies, and so take proof. */
export const levelTakesProof = (level: BrandAuthorizationLevel): boolean =>
  level !== 'unauthorized' && level !== 'former_dealer';

/**
 * Starting suggestions for the reseller distributor picker; free text is
 * always accepted. Mirrors `KNOWN_DISTRIBUTORS` in `src/types/brandAuthorization.ts`.
 */
export const KNOWN_DISTRIBUTORS: readonly string[] = [
  'ProMaster',
  'Photographic Research Organization (PRO)',
  'Imaging Alliance',
  'Fotosource Canada',
  'MAC Group',
  'HP Marketing Corp.',
];

/** One line that says who the store buys through or what the brand knows it by. */
export const accountSummary = (record: Pick<BrandAuthorization, 'level' | 'dealerAccountNumber' | 'distributorName' | 'distributorAccountNumber'>): string => {
  const parts: string[] = [];
  if (record.level === 'reseller' && record.distributorName) {
    parts.push(`via ${record.distributorName}${record.distributorAccountNumber ? ` (Acct ${record.distributorAccountNumber})` : ''}`);
  } else if (record.level === 'reseller' && record.distributorAccountNumber) {
    parts.push(`Distributor acct ${record.distributorAccountNumber}`);
  }
  if (record.dealerAccountNumber) parts.push(`Acct ${record.dealerAccountNumber}`);
  return parts.join(' · ');
};

export const BRAND_AUTHORIZATION_LEVELS: Array<{ value: BrandAuthorizationLevel; label: string; description: string }> = [
  { value: 'authorized_dealer', label: 'Authorized Dealer', description: 'Officially approved; warranty and marketing support' },
  { value: 'reseller', label: 'Reseller', description: 'Buys via authorized distributor or buying group' },
  { value: 'special_order', label: 'Special Order Only', description: 'Not regularly stocked; order on request' },
  { value: 'former_dealer', label: 'Former Dealer', description: 'No longer active or approved' },
  { value: 'unauthorized', label: 'Unauthorized', description: 'Not recognized by the manufacturer or distributor' },
];

export const levelLabel = (level: BrandAuthorizationLevel | string | undefined): string =>
  BRAND_AUTHORIZATION_LEVELS.find(l => l.value === level)?.label || 'Unknown level';

export const channelsLabel = (channels?: { online?: boolean; inStore?: boolean }): string => {
  const online = channels?.online !== false;
  const inStore = channels?.inStore !== false;
  if (online && inStore) return 'Online and in-store';
  if (online) return 'Online only';
  if (inStore) return 'In-store only';
  return 'No channel selected';
};

export const VERIFICATION_STATUS_META: Record<
  BrandVerificationStatus,
  { label: string; tone: 'positive' | 'caution' | 'problem' | 'neutral' | 'info' }
> = {
  unverified: { label: 'Not verified', tone: 'neutral' },
  pending: { label: 'Proof under review', tone: 'info' },
  verified: { label: 'Verified', tone: 'positive' },
  rejected: { label: 'Proof rejected', tone: 'problem' },
  expired: { label: 'Verification expired', tone: 'caution' },
};

export const verificationStatusOf = (record: BrandAuthorization): BrandVerificationStatus =>
  record.effectiveStatus || record.verification?.status || 'unverified';

export const METHOD_LABELS: Record<BrandVerificationMethod, string> = {
  self_declared: 'Self-declared',
  dealer_letter: 'Proof reviewed by Motzu',
  admin_assisted: 'Verified by Motzu from emailed proof',
  promaster_api: 'Promaster member API',
  brand_directory: "Found on the brand's authorized dealer list",
  dealer_directory_stale: "Approved from an older copy of the brand's dealer list",
  aputure_locator: 'Aputure dealer list',
  canon_wtb: 'Canon Where-to-Buy',
  fujifilm_locator: 'Fujifilm store locator',
  peak_design_locally: 'Peak Design Locally',
  gopro_locally: 'GoPro store locator',
  sony_pricespider: 'Sony dealer locator',
  hasselblad_partners: 'Hasselblad partners',
  nisi_locator: 'NiSi stockist locator',
  panasonic_authorized_list: 'Panasonic authorized retailers',
  rokinon_storeify: 'Rokinon store locator',
  smallrig_resellers: 'SmallRig reseller locator',
  tokina_shopcircle: 'Tokina store locator',
  thinktank_shopcircle: 'Think Tank store locator',
  tiffen_shopcircle: 'Tiffen store locator',
  tamron_dealers: 'Tamron dealer locator',
  benro_storemapper: 'Benro store locator',
  godox_storemapper: 'Godox USA dealer locator',
  promaster_storerocket: 'ProMaster store locator',
  pelican_locator: 'Pelican dealer locator',
  phottix_storerocket: 'Phottix store locator',
  smallhd_cs: 'SmallHD reseller map',
  polaroid_experience_stores: 'Polaroid I-2 Experience Stores',
  leica_locator: 'Leica dealer locator',
  lomography_stores: 'Lomography store locator',
  manfrotto_locator: 'Manfrotto store locator',
  lowepro_locator: 'Lowepro where-to-buy',
  zeiss_locator: 'Zeiss dealer locator',
  laowa_locator: 'Laowa US authorized dealers',
  superior_seamless_locator: 'Superior Seamless store locator',
};

export const methodLabel = (method?: string | null): string =>
  (method && METHOD_LABELS[method as BrandVerificationMethod]) || method || 'Unknown';

/** Mirrors the API's proof-link check so the dialog can say so before Save. */
export const isValidProofLink = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (!/^https:\/\//i.test(trimmed)) return false;
  try {
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
};

/** Storage path the client uploads proof to; the API only accepts this prefix. */
export const proofStoragePath = (organizationId: string, brandId: string, fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'pdf';
  return `brand-authorization-proof/${organizationId}/${brandId}/${Date.now()}.${extension}`;
};

/** Mirrors storage.rules: brand-authorization-proof accepts PDF or image up to 10MB. */
export const PROOF_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const PROOF_ACCEPTED_TYPES = 'application/pdf,image/*';

export const isAcceptedProofFileType = (file: File): boolean =>
  file.type === 'application/pdf' || file.type.startsWith('image/');

/** Same proof-link rule as the brand dialog's design: https, a host with a dot. */
export const isCompleteProofLink = (value: string): boolean => /^https:\/\/\S+\.\S+/i.test(value.trim());

/**
 * What "Verify now" found. Mirrors `BrandAuthorizationCheckResult` in
 * `src/types/brandAuthorization.ts` (POST /store/brand-authorizations/:brandId/check).
 */
export type BrandAuthorizationCheckOutcome =
  | 'verified'
  | 'confirming'
  | 'not_found'
  | 'no_list'
  | 'proof_only'
  | 'held'
  | 'unavailable'
  | 'rejected';

export interface BrandAuthorizationCheckResult {
  outcome: BrandAuthorizationCheckOutcome;
  path: BrandVerificationPath;
  checkedAt: string;
  method?: BrandVerificationMethod | null;
  verifiedAt?: string | null;
  match?: { dealerName: string | null; city: string | null; state: string | null } | null;
  reason?: 'stale_list' | 'partial_name' | 'awaiting_confirmation' | null;
  listBrandName?: string | null;
  listDate?: string | null;
  unavailableReason?: 'lookup_failed' | 'no_address' | 'not_configured' | null;
}

/** Methods that confirmed the claim on an imported dealer list (every other automatic method is a live locator). */
const LIST_METHODS: readonly BrandVerificationMethod[] = ['brand_directory', 'dealer_directory_stale'];
const NON_LOCATOR_METHODS: readonly BrandVerificationMethod[] = [
  ...LIST_METHODS,
  'self_declared',
  'dealer_letter',
  'admin_assisted',
  'promaster_api',
];

/** Where a verification came from, for the dialog's "Verified on …" line. */
export const verificationSourceOf = (method?: BrandVerificationMethod | null): 'locator' | 'list' | 'motzu' => {
  if (!method) return 'motzu';
  if (LIST_METHODS.includes(method)) return 'list';
  if (NON_LOCATOR_METHODS.includes(method)) return 'motzu';
  return 'locator';
};

/**
 * The check result a record already carries when the dialog opens, so a
 * verified or list-matched brand reads the same as right after a check.
 * Null when nothing has been found yet (the card starts idle).
 */
export const checkResultFromRecord = (record: BrandAuthorization | null | undefined): BrandAuthorizationCheckResult | null => {
  if (!record) return null;
  const path = record.verificationPath || 'letter_only';
  const checkedAt = record.directoryMatch?.checkedAt || record.verification.verifiedAt || new Date().toISOString();
  const status = verificationStatusOf(record);
  if (status === 'verified') {
    const listed = record.directoryMatch;
    const fromList = LIST_METHODS.includes(record.verification.method) && listed && listed.confidence !== 'none';
    return {
      outcome: 'verified',
      path,
      checkedAt,
      method: record.verification.method,
      verifiedAt: record.verification.verifiedAt ?? null,
      match: fromList ? { dealerName: listed.dealerName, city: listed.city, state: listed.state } : null,
    };
  }
  if (status === 'rejected') return { outcome: 'rejected', path, checkedAt };
  const tag = verificationTagOf(record);
  if (tag.kind === 'list_match' && tag.match) {
    return {
      outcome: 'confirming',
      path,
      checkedAt,
      match: { dealerName: tag.match.dealerName, city: tag.match.city, state: tag.match.state },
      reason: tag.match.reason,
      listBrandName: tag.listBrandName ?? null,
    };
  }
  return null;
};

/** "Lakeside Camera, Madison WI" from a list row. */
export const matchedStoreLabel = (match?: { dealerName: string | null; city: string | null; state: string | null } | null): string => {
  if (!match?.dealerName) return '';
  const place = [match.city, match.state].filter(Boolean).join(' ');
  return place ? `${match.dealerName}, ${place}` : match.dealerName;
};
